<template>
    <div class="items">
        <div v-if="!empty">
            <el-table-draggable handle=".handle" @drop="dropped">
                <el-table class="table-responsive table-flush"
                        :show-header="false"
                        :data="ctrl.rows"
                        ref="items">

                    <el-table-column class-name="item">
                        <template v-slot="{row}">

                            <table width="100%" v-if="!row.group">
                                <tr>
                                    <td class="type" width="55">
                                        <el-popover placement="top">
                                            <a slot="reference" href="#" @click.prevent="">
                                                <i :class="types[row.type].icon+' text-'+types[row.type].color"
                                                    style="font-size:1.6rem;"
                                                ></i>
                                            </a>
                                            <h5 class="text-center mb-0">
                                                {{ types[row.type].name[lang] }}
                                            </h5>
                                        </el-popover>
                                    </td>
                                    <td class="description">
                                        <div :class="' pl-3 border-left border-'+types[row.type].color">
                                            <div class="tinymce" v-html="row.description"></div>
                                        </div>

                                        <div class="mb-2" v-if="row.competency_ids.length">
                                            <span v-for="competencyId in row.competency_ids" :key="competencyId">
                                                <badge type="primary" class="mr-1" v-if="competencyExists(competencyId)">
                                                    {{ competencyName(competencyId) }}
                                                </badge>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                            <div class="ml-4" v-if="row.group">
                                <h3 :class="row.title ? '' : 'text-muted'" class="mb-2 mt-2">
                                    {{ row.title ? row.title : 'Actions' }}
                                    <badge type="secondary" class="flight"
                                            v-for="(flight, index) in row.flights"
                                            :key="index">
                                        <i class="fa fa-plane"></i>
                                        {{ flight }}
                                    </badge>
                                </h3>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column align="right" width="225" class-name="actions">
                        <template v-slot="{row}">
                            <div class="table-actions">

                                <a href="#" class="table-action text-primary" v-if="canEdit && row.group"
                                    @click.prevent="createItem(row)"
                                >
                                    <i class="tim-icons icon-e-add"></i>
                                </a>

                                <a href="#" class="table-action text-default handle" v-if="canEdit">
                                    <i class="tim-icons icon-drag-31"></i>
                                </a>

                                <a href="#" class="table-action text-danger" v-if="canEdit"
                                    @click.prevent="deleteItem(row.id)"
                                >
                                    <i class="tim-icons icon-simple-remove"></i>
                                </a>

                                <a href="#" class="table-action text-primary" v-if="canEdit"
                                    @click.prevent="updateItem(row)"
                                >
                                    <i class="tim-icons icon-pencil"></i>
                                </a>

                                <actions-dropdown :actions="actions(row)"></actions-dropdown>

                            </div>
                        </template>
                    </el-table-column>
                </el-table>
            </el-table-draggable>
        </div>

        <div class="text-right pr-4 pb-3 pt-3" :class="{'pt-3': ctrl.rows.length == 0}" v-if="loaded && canEdit">

            <base-button type="primary" size="sm" @click="createGroup()" v-if="!timeslot.is_instruction">
                <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                <span class="btn-inner--text">{{ $t('common.items-group') }}</span>
            </base-button>

            <base-button type="primary" size="sm" @click="createItem()">
                <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                <span class="btn-inner--text">{{ $t('common.item') }}</span>
            </base-button>
        </div>
    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import ElTableDraggable from 'element-ui-el-table-draggable'

    export default {

        components: {
            ElTableDraggable
        },

        props: {

            timeslot: {
                type: Object,
                required: true
            },

            lang: {
                type: String,
                default: "en"
            },
            
            types: {
                type: Object,
                required: true
            },
            
            competencies: {
                type: Object,
                required: true
            },
            
            degree: {
                type: Object,
                required: true
            },
        },

        data() {
            return {
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/socrates/api/front/log-items',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    query: {
                        filters: { timeslot_id: this.timeslot.id },
                        sort: ['position'],
                    },
                    autolist: false,    // Don't get the items now, they are in the timeslot.
                }),
                ctrlGroup: new CrudController({
                    endpoint: '/socrates/api/front/log-items',
                    mapData: this.mapData,
                    unmapData: this.unmapGroupData,
                    autolist: false,
                    callbacks: {
                        created: this.refresh,
                        updated: this.refresh,
                        duplicated: this.refresh,
                    }
                }),
                selectedGroupId: null,
            }
        },

        computed: {

            shared() {
                return this.degree.shared && this.$auth.user.owner_id && this.$auth.user.owner_id != this.degree.owner_id
            },

            canEdit() {
                return !this.shared && this.degree.status != 'archived'
            },
            
            canEditAll() {
                return !this.shared && this.degree.status != 'active' && this.degree.status != 'archived'
            },

            empty() {
                return this.ctrl.rows.length == 0
            },

            lastPosition() {
                let maxPosition = 0
                this.ctrl.rows.forEach(item => {
                    maxPosition = Math.max(maxPosition, item.position)
                })
                return maxPosition + 1
            }
        },

        created() {
            if (this.timeslot.items) {
                this.ctrl.init(this, 'ctrl')
                this.ctrl.setRows(this.timeslot.items)
                this.ctrl.autolist = true       // Auto-refresh items on CRUD operations.
                this.loaded = true
            } else {
                this.ctrl.autolist = true       // Auto-refresh items on CRUD operations.
                this.ctrl.init(this, 'ctrl').then(resp => {
                    this.loaded = true
                })
            }
            // Secondary controller.
            this.ctrlGroup.init(this, 'ctrlGroup')
        },

        methods: {

            competencyExists(competencyId) {
                return this.competencies.find(comp => comp.id == competencyId) !== undefined
            },

            competencyName(competencyId) {
                return this.competencies.find(comp => comp.id == competencyId)['name_' + this.degree.lang]
            },

            refresh() {
                this.ctrl.list()
            },

            actions(row) {
                let actions = []
                if (this.canEdit) {
                    actions.push({
                        label: this.$t('common.duplicate'),
                        handler: this.duplicateItem,
                        data: row
                    })
                }
                actions.push({
                    label: this.$t('common.copy'),
                    handler: this.copyItem,
                    data: row.id
                })
                return actions
            },

            createItem(group) {
                this.selectedGroupId = group ? group.id : null
                this.$emit('selected', this.ctrl)
                this.ctrl.create()
            },

            createGroup() {
                this.$emit('selectedGroup', this.ctrlGroup)
                this.ctrlGroup.create()
            },

            updateItem(row) {
                if (!row.group) {
                    this.$emit('selected', this.ctrl)
                    this.ctrl.update(row.id)
                } else {
                    this.$emit('selectedGroup', this.ctrlGroup)
                    this.ctrlGroup.update(row.id)
                }
            },

            deleteItem(id) {
                this.$emit('selected', this.ctrl)
                this.ctrl.delete(id)
            },

            duplicateItem(row) {
                if (row.group) {
                    this.$emit('selectedGroup', this.ctrlGroup)
                    this.ctrlGroup.duplicate(row.id)
                } else {
                    this.$emit('selected', this.ctrl)
                    this.ctrl.duplicate(row.id)
                }
            },

            copyItem(id) {
                axios.post(this.ctrl.client.endpoint + '/' + id + '/copy')
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            dropped(data) {
                let ids = data.list.map(item => item.id)
                axios.post('/socrates/api/front/log-timeslots/'+this.timeslot.id+'/reorder-items', { ids })
                .then(resp => {
                    this.ctrl.list()
                })
                .catch(err => {
                    //this.ctrl.list()
                    this.$auth.handleErrorVm(err, this)
                });
            },
            
            mapData(item) {
                return {
                    id: item ? item.id : null,
                    group: item ? item.group : false,
                    flights: item ? item.flights : [],
                    title: item ? item.title : '',
                    description: item ? item.description : '',
                    type: item ? item.type : null,
                    position: item ? item.position : this.lastPosition,
                    competency_ids: item ? this.competencyIds(item) : null,
                }
            },

            unmapData(data) {
                let item = {...data}
                item.timeslot_id = this.timeslot.id
                item.group_id = this.selectedGroupId
                return item
            },

            unmapGroupData(data) {
                let item = {...data}
                item.group = true
                item.timeslot_id = this.timeslot.id
                item.flights = data.flights.filter(flight => flight && flight.trim() != '')
                return item
            },

            competencyIds(item) {
                return item.competency_relations.map(relation => relation.competency_id.toString())
            },
        }
    }
</script>

<style scoped>

    .items >>> tr.el-table__row:hover,
    .items >>> tr.el-table__row:hover tr {
        background-color: #f7fafc;
    }
    .items >>> tr.el-table__row > td {
        padding-top: 0.5rem !important;
        padding-bottom: 0.5rem !important;
    }
    .items >>> tr.el-table__row > td td {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }
    .items >>> td {
        padding-right: 0;
    }
    .items >>> td.item {
        padding: 0;
    }
    .items >>> td.type {
        vertical-align: top;
    }
    .items >>> td.description {
        padding-left: 10px;
    }
    .items >>> td.description .border-left {
        border-width: 2px !important;
    }
    .items >>> td.actions {
        padding-right: 1.5rem;
    }
    .items >>> .el-table::before {
        background-color: #fff;
    }
    .badge.flight {
        margin-left: 10px;
        color: #424c55;
    }
    
</style>